import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ReleaseNote } from 'src/app/interfaces/modal.interface';
import { notNull, onlyTruthy } from 'src/app/utils/rxjs-custom-operators/rxjs-custom-operators.utils';
import { ApiService } from '../api/api.service';
import { CcAdminApiService } from '../ccAdmin/cc-admin-api.service';
import { directory } from 'src/assets/release-notes/directory';
import { AgreementsModalInvokerService } from '../agreements-modal-invoker/agreements-modal-invoker.service';
import { ContentModalService } from '@msi/cobalt';
import { NewFeatureModalComponent } from 'src/app/components/new-feature-modal/new-feature-modal.component';

@Injectable({
	providedIn: 'root',
})
export class NewFeatureModalService {
	private directory: ReleaseNote[] = directory.sort((a, b) => (a.releaseTitle < b.releaseTitle ? 1 : -1));

	// Subject to track release notes
	private releaseNotesSubject: BehaviorSubject<ReleaseNote[]> = new BehaviorSubject(null);
	public get releaseNotes$(): Observable<ReleaseNote[]> {
		return this.releaseNotesSubject.asObservable().pipe(notNull());
	}

	constructor(
		private apiService: ApiService,
		private ccAdminApiService: CcAdminApiService,
		private agreementsModalInvokerService: AgreementsModalInvokerService,
		private contentModalService: ContentModalService
	) {
		this.ccAdminApiService.graphInitialized$.pipe(take(1)).subscribe(() => {
			// If user has not viewed the latest release notes, preload them and prompt the user to view
			if (localStorage.getItem('latestReleaseNotesViewed') !== this.directory[0].releaseTitle) {
				this.loadAllReleaseNotes();
				this.releaseNotes$.pipe(take(1)).subscribe(() => {
					this.agreementsModalInvokerService.hasAcceptedAllAgreements$.pipe(onlyTruthy(), take(1)).subscribe(() => {
						this.openModal();
					});
				});
			}
		});
	}

	/**
	 * Open the New Feature modal to a given index
	 */
	public openModal(i: number = 0) {
		this.contentModalService.open(NewFeatureModalComponent, { data: { selectedMediaIndex: i } });
	}

	/**
	 * Load the HTML content for all modal pages starting with
	 * the specified page index and working outwards from there
	 */
	public loadAllReleaseNotes(startIndex: number = 0) {
		this.ccAdminApiService.graphInitialized$.pipe(take(1)).subscribe(() => {
			this.releaseNotesSubject.next(this.directory);
			this.loadReleaseNote(startIndex);
			for (let i = 1; i < Math.max(this.directory.length - startIndex, startIndex); i++) {
				// Load later entry
				if (startIndex + i < this.directory.length) {
					this.loadReleaseNote(startIndex + i);
				}
				// Load earlier entry
				if (startIndex - i >= 0) {
					this.loadReleaseNote(startIndex - i);
				}
			}
		});
	}

	/**
	 * Load the HTML content of a specific modal page
	 */
	public loadReleaseNote(i: number) {
		if (this.directory?.length && !this.directory[i].fileContents) {
			this.apiService.getReleaseNotes(this.directory[i].fileName).subscribe(content => {
				this.directory[i].fileContents = content;
				this.releaseNotesSubject.next(this.directory);
			});
		}
	}
}

// Copyright (c) Motorola Solutions Inc. All Rights Reserved.
